/// <reference path="../../../../typings/browser.d.ts" />

namespace Application.Controllers {
  'use strict';

  import IStickyConnection = Models.IStickyConnection;
  import StickyConnection = Models.StickyConnection;
  import IBlock = Models.IBlock;
  import IBlockConnection = Application.Models.IBlockConnection;
  import ConnectionType = Application.Models.ConnectionType;
  import Utils = Common.Utils;
    import IPresetPathBlock = Application.Models.IPresetPathBlock;

  interface IOffsettedRepo {
    totalLoaded: number;
    firstLoadFinished: boolean;
    loading: boolean;
  }

  interface IAdminPresetPathsControllerRouteParamsService extends ng.route.IRouteParamsService {
    type: string;
  }

  /**
   * PresetPathsController class
   */
  export class PresetPathsController {
    public static NUM_REQUESTS: number = 20;
    public static $name: string = 'presetPathsController';
    public static $inject: string[] = [
      'presetPathService',
      '$timeout',
      '$q',
      '$window',
      'ModalService',
      'blockService',
      'networkNodeService',
      '$routeParams',
    ];

    public stickyConnections: IStickyConnection[] = [];

    private currentPage: number;
    private moreData: boolean;
    private dataLoaded: boolean;
    private sortType: string;
    private searchText: string;
    private timeoutSearch: ng.IPromise<void>;
    private presetPathType: string;
    private tsoipBlockTypes: number[] = [5, 11, 14];
    private loading: boolean = false;

    private searchTextForHighlight: string;

    private stickyConnectionsRepo: IOffsettedRepo = {
      totalLoaded: 0,
      firstLoadFinished: false,
      loading: true
    };

    private defaultRoute = 'admin/preset-paths/srt';
    private srtButtonText: string = 'Create SRT Preset Path';
    private tsoipButtonText: string = 'Create TS Preset Path';
    private bulkSrtButtonText: string = 'Create Bulk SRT Preset Path';
    private bulkTsoipButtonText: string = 'Create Bulk TS Preset Path';
    private presetPathbuttonText: string;
    private bulkPresetPathbuttonText: string;


    constructor(
      private presetPathService: Services.PresetPathService,
      private $timeout: ng.ITimeoutService,
      private qService: ng.IQService,
      private $window: ng.IWindowService,
      private modalService: ngModal.IModalService,
      private blockService: Services.BlockService,
      private networkNodeService: Services.NetworkNodeService,
      private $routeParams: IAdminPresetPathsControllerRouteParamsService
    ) {
      Utils.redirectToCorrectRoute(!this.isValidTab(this.$routeParams.type), this.defaultRoute);
      this.onTabChange(this.$routeParams.type);
      this.initPresetPaths();
    }

    public needReTake(stickyConnection: StickyConnection): boolean {
      if(PresetPathsController.containsRetake(stickyConnection.origBlockConnection)) {
        return stickyConnection.origBlockConnection.parameters.needReTake;
      }
      if(PresetPathsController.containsRetake(stickyConnection.destBlockConnection)) {
        return stickyConnection.destBlockConnection.parameters.needReTake;
      }
      return false;
    }

    public getPassword(stickyConnection: StickyConnection): string {
      if(PresetPathsController.containsPassphrase(stickyConnection.origBlockConnection)) {
        return stickyConnection.origBlockConnection.parameters.passphrase;
      } else if(PresetPathsController.containsPassphrase(stickyConnection.destBlockConnection)){
        return stickyConnection.destBlockConnection.parameters.passphrase;
      }
      return "";
    }

    private static containsPassphrase(blockConnection: IBlockConnection): boolean {
      return blockConnection !== undefined
          && blockConnection.parameters !== undefined
          && blockConnection.parameters.passphrase !== undefined;
    }

    private static containsRetake(blockConnection: IBlockConnection): boolean {
      return blockConnection !== undefined
          && blockConnection.parameters !== undefined
          && blockConnection.parameters.needReTake !== undefined;
    }

    public async launchBulkAddPresetPathsModal(): Promise<void> {
      if(this.loading) {
        return;
      }
      this.loading = true;
      let blocks: IPresetPathBlock[] = this.presetPathType === ConnectionType.SRT ?
          await this.presetPathService.getPresetPathBlocksByConnection(this.presetPathType) :
          await this.presetPathService.getPresetPathBlocksByConnectionAndBlockTypes(this.presetPathType, this.tsoipBlockTypes);
      this.modalService
          .showModal({
            templateUrl: Controllers.BulkAddPresetPathsModalController.templateUrl,
            controller: Controllers.BulkAddPresetPathsModalController.$name,
            inputs: {
              blocks: blocks,
              presetPathType: this.presetPathType
            }
          })
          .then((modal: ngModal.IModalObject) => {
            const modalElem: UIkit.ModalElement = UIkit.modal(
                Controllers.BulkAddPresetPathsModalController.id,
                { bgclose: false, modal: false, keyboard: false }
            );
            modalElem.show();
            modal.close.then(() => {
              this.loading = false;
              return this.loadStickyConnectionsWithPageSize(
                  PresetPathsController.NUM_REQUESTS,
                  this.currentPage
              );
            })
          });
    }


    public async removeMonitorBlocksFromSinkBlocks(sinkBlocks: IBlock[]){
      const monitorDevices = await this.networkNodeService.getMonitorNodes();
      if(monitorDevices.length > 0) {
        return sinkBlocks.filter(sinBlock => {
          return monitorDevices.some( monitorDevice => {
            return monitorDevice.id != sinBlock.networkNodeHref
          });
        });
      }
      else {
        return sinkBlocks;
      }
  }

    public async launchAddPresetPathModal(): Promise<void> {
      if(this.loading) {
        return;
      }
      this.loading = true;
      let blocks: IPresetPathBlock[] = this.presetPathType === ConnectionType.SRT ?
        await this.presetPathService.getPresetPathBlocksByConnection(this.presetPathType) :
        await this.presetPathService.getPresetPathBlocksByConnectionAndBlockTypes(this.presetPathType, this.tsoipBlockTypes);

      this.modalService
        .showModal({
          templateUrl: Controllers.AddPresetPathsModalController.templateUrl,
          controller: Controllers.AddPresetPathsModalController.$name,
          inputs: {
            blocks: blocks,
            presetPathType: this.presetPathType
          }
        })
        .then((modal: ngModal.IModalObject) => {
          const modalElem: UIkit.ModalElement = UIkit.modal(
            Controllers.AddPresetPathsModalController.id,
            { bgclose: false, modal: false, keyboard: false }
          );
          modalElem.show();
          modal.close.then(
            (result: { name: string; srcConn: number; dstConn: number }) => {
              this.loading = false;
              modalElem.hide();
              if (result) {
                this.presetPathService
                  .createStickyConnection({
                    name: result.name,
                    origBlockConnectionId: result.srcConn,
                    destBlockConnectionId: result.dstConn
                  })
                  .then(() => {
                    return this.loadStickyConnectionsWithPageSize(
                      PresetPathsController.NUM_REQUESTS,
                      this.currentPage
                    );
                  });
              }
            }
          );
        });
    }

    public editPresetPathSettings(stickyConnection: IStickyConnection): void {
      this.modalService
          .showModal({
            templateUrl: Controllers.PresetPathSettingsModalController.templateURL,
            controller: Controllers.PresetPathSettingsModalController.$name,
            inputs: {
              stickyConnection: stickyConnection,
              presetPathType: this.presetPathType
            }
          })
          .then((modal: ngModal.IModalObject) => {
            let modalElem: UIkit.ModalElement = UIkit.modal(Application.Controllers.PresetPathSettingsModalController.ID,
                {bgclose: false, modal: false, keyboard: false});
            modalElem.show();
            modal.close.then((result?: string) => {
              if (result) {
                stickyConnection.origBlockConnection.parameters.passphrase = result;
                stickyConnection.origBlockConnection.parameters.needReTake = true;
              }
              this.initPresetPaths();
              modalElem.hide();
            });
          });
    }

    public removeStickyConnection(stickyConnection: IStickyConnection): void {
      if (stickyConnection.isConnected()) {
        this.modalService
          .showModal({
            templateUrl:
              Application.Controllers.InfoModalController.templateURL,
            controller: Application.Controllers.InfoModalController.$name,
            inputs: {
              title: 'PRESET PATH INFO',
              description:
                'This Preset Path is involved in a pipe connection. Please disconnect its blocks before removing it.'
            }
          })
          .then((modal: ngModal.IModalObject) => {
            let modalElem: UIkit.ModalElement = UIkit.modal(
              Application.Controllers.InfoModalController.ID,
              { bgclose: false, modal: false, keyboard: false }
            );
            modalElem.show();
            modal.close.then((result: Object) => {
              modalElem.hide();
            });
          });
      } else {
        this.modalService
          .showModal({
            templateUrl: Controllers.ConfirmModalController.templateURL,
            controller: Controllers.ConfirmModalController.$name,
            inputs: {
              title: 'DELETE PRESET PATH',
              description:
                "This preset path will be deleted and won't be available anymore",
              name: '',
              type: 'warning'
            }
          })
          .then((modal: ngModal.IModalObject) => {
            let modalElem: UIkit.ModalElement = UIkit.modal(
              Controllers.ConfirmModalController.ID,
              { bgclose: false, modal: false, keyboard: false }
            );
            modalElem.show();
            modal.close.then((result: string) => {
              modalElem.hide();
              if (result) {
                this.presetPathService
                  .removeStickyConnection(stickyConnection)
                  .then((response: boolean) => {
                    this.stickyConnections.forEach(
                      (sc: IStickyConnection, index: number) => {
                        if (
                          Common.Utils.idFromHref(sc.href) ===
                          Common.Utils.idFromHref(stickyConnection.href)
                        ) {
                          this.stickyConnections.splice(index, 1);
                        }
                      }
                    );
                    this.loadStickyConnectionsWithPageSize(
                      PresetPathsController.NUM_REQUESTS,
                      this.currentPage
                    );
                  });
              }
            });
          });
      }
    }

    public getBlockIcon(block: Models.IBlock): string {
      if (
        block.networkNodeTypeHref === undefined ||
        block.networkNodeTypeHref === ''
      ) {
        return 'images/iconmonstr-server-4-240-not-checked.png';
      } else {
        return `images/nodeTypes/${block.networkNodeTypeHref}.png`;
      }
    }

    public searchAsType(): void {
      if (this.searchText !== undefined) {
        this.$timeout.cancel(this.timeoutSearch);
        this.timeoutSearch = this.$timeout(() => {
          this.applyFilter();
          this.loadStickyConnections();
        }, 500);
      }
    }

    public applyFilter(): void {
      this.stickyConnections = [];
      this.currentPage = 0;
      this.loadStickyConnections();
      this.searchTextForHighlight = this.searchText;
    }

    private loadStickyConnections(): ng.IPromise<void> {
      this.dataLoaded = false;
      let pageSizeToRequest: number = PresetPathsController.NUM_REQUESTS;
      return this.loadStickyConnectionsWithPageSize(
        pageSizeToRequest,
        this.currentPage
      );
    }

    private loadStickyConnectionsWithPageSize(
      pageSizeToRequest: number,
      pageNumber: number
    ): ng.IPromise<void> {
      this.stickyConnectionsRepo.loading = true;
      return this.presetPathService
        .getFilteredStickyConnections(
          pageNumber,
          pageSizeToRequest,
          this.searchText,
          this.presetPathType
        )
        .then((stickyConnections: Models.IPaginatedList<IStickyConnection>) => {
          if (!this.stickyConnections || this.stickyConnections.length === 0)
            this.stickyConnections = stickyConnections.elements;
          else {
            stickyConnections.elements.forEach((sc: StickyConnection) => {
              let found: StickyConnection[] = this.stickyConnections.filter(
                (sConnections: StickyConnection) => {
                  return sConnections.href === sc.href;
                }
              );
              if (found.length === 0) this.stickyConnections.push(sc);
            });
          }

          this.moreData = stickyConnections.page.totalPages > pageNumber + 1;

          this.dataLoaded = true;
          this.stickyConnectionsRepo.loading = false;
          this.stickyConnectionsRepo.totalLoaded = this.stickyConnections
            ? this.stickyConnections.length
            : 0;
        });
    }

    private async initPresetPaths(): Promise<void> {
      this.stickyConnections = [];
      this.searchText = '';
      this.sortType = 'creationDate';
      this.currentPage = 0;
      this.dataLoaded = false;

      await this.loadStickyConnectionsWithPageSize(
        PresetPathsController.NUM_REQUESTS,
        this.currentPage
      );
      this.launchScrollEvent();
    }

    private launchScrollEvent(): void {
      angular.element(this.$window).bind('scroll', () => {
        if (
          this.moreData &&
          !this.stickyConnectionsRepo.loading &&
          this.$window.innerHeight + this.$window.scrollY >=
            document.body.scrollHeight - 100
        ) {
          this.stickyConnectionsRepo.loading = true;
          this.loadMore().then(() => {
            this.stickyConnectionsRepo.loading = false;
          });
        }
      });
    }

    private loadMore(): ng.IPromise<void> {
      this.dataLoaded = false;
      let pageSizeToRequest: number = PresetPathsController.NUM_REQUESTS;
      this.currentPage++;
      return this.loadStickyConnectionsWithPageSize(
        pageSizeToRequest,
        this.currentPage
      );
    }

    /**
     * @param tab Tab name
     * changes the route for the page and the buttons text
     */
    private onTabChange(tab: string) {
      if(this.isValidTab(tab)) {
        this.$routeParams.type = tab;
        this.presetPathType = this.isSrtTab() ? ConnectionType.SRT : ConnectionType.TSOIP;
        this.presetPathbuttonText = this.isSrtTab() ? this.srtButtonText : this.tsoipButtonText;
        this.bulkPresetPathbuttonText = this.isSrtTab() ? this.bulkSrtButtonText : this.bulkTsoipButtonText;
      }
    }

    /**
     * @returns true if the selected tab is SRT
     */
    private isSrtTab(): boolean {
      return this.$routeParams.type === ConnectionType.SRT.toLowerCase();
    }

    /**
     * Checks if the selected tab for routing is correct.
     * @param tab selected tab for routing
     * @returns 
     */
    private isValidTab(tab: string): boolean {
      return tab && (this.$routeParams.type === ConnectionType.SRT.toLowerCase() || this.$routeParams.type === ConnectionType.TSOIP.toLowerCase());
    }
  }
}
